/**
 * LE DÉFI ANTI-ROBOT DE LA CONSOLE — Cloudflare Turnstile, AB#884.
 *
 * La console se connecte par `POST /v1/auth/login` et demande ses liens par
 * `POST /v1/auth/mot-de-passe-oublie`, depuis un NAVIGATEUR : le jour où l'API
 * exige le défi (`ANTI_ROBOT_DEFI=oui`), une console sans widget serait
 * refusée. Même mécanique que l'app des familles
 * (`apps/mobile/src/features/anti-robots/`) : le widget dépose son jeton ici,
 * le client d'API le prend et l'ajoute au corps.
 *
 * Sans `VITE_TURNSTILE_SITE_KEY` : rien n'est rendu, aucun script chargé.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';

export const URL_SCRIPT_TURNSTILE =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface Turnstile {
  render(el: HTMLElement, options: Record<string, unknown>): string;
  reset(id?: string): void;
  remove(id?: string): void;
}

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

let widgets = 0;
let jeton: string | null = null;
const attente = new Set<(j: string | null) => void>();
const apresConsommation = new Set<() => void>();
const apresRefus = new Set<() => void>();

export function cleDeSite(): string {
  return (import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '').trim();
}

/** Le jeton à joindre, consommé ; attend au plus `attenteMs`. Sans widget : `undefined`. */
export async function prendreJeton(attenteMs = 8_000): Promise<string | undefined> {
  if (widgets === 0) return undefined;
  const pris =
    jeton ??
    (await new Promise<string | null>((resolve) => {
      const fin = (j: string | null) => {
        clearTimeout(minuterie);
        resolve(j);
      };
      const minuterie = setTimeout(() => {
        attente.delete(fin);
        resolve(null);
      }, attenteMs);
      attente.add(fin);
    }));
  jeton = null;
  for (const e of apresConsommation) e();
  return pris ?? undefined;
}

export function deposerJeton(j: string | null): void {
  jeton = j;
  if (j !== null) {
    for (const r of attente) r(j);
    attente.clear();
  }
}

export function signalerRefus(): void {
  for (const e of apresRefus) e();
}

let chargement: Promise<Turnstile> | null = null;
function chargerTurnstile(): Promise<Turnstile> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (chargement) return chargement;
  chargement = new Promise<Turnstile>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = URL_SCRIPT_TURNSTILE;
    s.async = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('absent')));
    s.onerror = () => {
      chargement = null;
      reject(new Error('injoignable'));
    };
    document.head.appendChild(s);
  });
  return chargement;
}

export const MESSAGE_REFUS =
  'La vérification anti-robot n’a pas abouti. Elle se refait : patientez quelques secondes, puis réessayez.';

export function DefiAntiRobot(): ReactNode {
  const cle = cleDeSite();
  const hote = useRef<HTMLDivElement | null>(null);
  const [refus, setRefus] = useState(false);

  useEffect(() => {
    if (cle === '') return undefined;
    widgets += 1;
    let vivant = true;
    let id: string | undefined;
    let t: Turnstile | undefined;
    const retirer = () => {
      widgets = Math.max(0, widgets - 1);
      if (widgets === 0) {
        jeton = null;
        for (const r of attente) r(null);
        attente.clear();
      }
    };
    let retire = false;
    const retirerUneFois = () => {
      if (!retire) retirer();
      retire = true;
    };
    chargerTurnstile()
      .then((turnstile) => {
        if (!vivant || !hote.current) return;
        t = turnstile;
        id = turnstile.render(hote.current, {
          sitekey: cle,
          appearance: 'interaction-only',
          language: 'fr',
          callback: (j: string) => {
            setRefus(false);
            deposerJeton(j);
          },
          'expired-callback': () => deposerJeton(null),
          'error-callback': () => deposerJeton(null),
        });
      })
      .catch(retirerUneFois);
    const reset = () => {
      if (t && id !== undefined) t.reset(id);
    };
    const refuse = () => setRefus(true);
    apresConsommation.add(reset);
    apresRefus.add(refuse);
    return () => {
      vivant = false;
      apresConsommation.delete(reset);
      apresRefus.delete(refuse);
      retirerUneFois();
      if (t && id !== undefined) t.remove(id);
    };
  }, [cle]);

  if (cle === '') return null;
  return (
    <div className="field" data-testid="defi-anti-robot">
      <div ref={hote} />
      {refus ? (
        <p className="form-error" role="alert">
          {MESSAGE_REFUS}
        </p>
      ) : null}
    </div>
  );
}
